/*
 * twc:view_item_list — the product list a visitor sees on a collection or
 * search page (SHOPIFY-1238).
 *
 * Shopify's standard events report each product card on its own
 * (product_viewed as a card scrolls into view) and never the list as a
 * whole, so GA4's view_item_list has nothing to feed it. This script reads
 * the cards the page renders and publishes the list through
 * window.twcTrack (snippets/twc-analytics-bridge.liquid), which renders
 * this file with `defer` on the collection and search templates and hands
 * it the list's id and name as data attributes on the script tag.
 *
 * One event per list the visitor sees:
 *
 *   trigger 'load'       the page's own cards, once, on load
 *   trigger 'sort'       after a sort change re-rendered the grid (facets.js
 *   trigger 'filter'     dispatches shopify:collection:update or
 *                        shopify:search:update on the document with a
 *                        `promise` that settles when the section has
 *                        re-rendered; the full list is read after it)
 *   trigger 'load_more'  the cards infinite scroll appended (Horizon's
 *                        paginated-list.js appends li[data-page] into
 *                        [ref="grid"] with no event, so a MutationObserver
 *                        watches the grid); only the NEW cards are sent
 *
 * Payload:
 *   { list_id, list_name, trigger, page, sort_key, filters, query, currency,
 *     items: [{ product_id, variant_id, title, handle, price, position }],
 *     page_path }
 *
 * Each card is an li[data-product-id] under #MainContent whose
 * [view-event-payload] attribute (Shopify's product | standard_event_data:
 * 'view', rendered by every product card) carries the product; gid
 * prefixes are stripped to the numeric id. On a list card that payload's
 * selectedVariant is null, so variant_id and price fall back to the card's
 * own data-twc-variant-id / data-twc-price (snippets/twc-plp-card.liquid
 * writes them from selected_or_first_available_variant, price in currency
 * units) and, failing those, the variant from the card link's ?variant=.
 * Horizon's own card (the search page) carries no such attributes, so as
 * a last resort the price is parsed from its rendered price element
 * (snippets/price.liquid: `.price-item--sale.price` on sale, else `.price`,
 * never the compare-at price): the first money-looking run of digits,
 * commas dropped, "$1,234.50" → 1234.5; null when nothing parses. position
 * is the card's 1-based index in the whole grid, also on 'load_more'.
 * sort_key, filters (every filter.* parameter as {name, value}) and query
 * come from the URL, which facets.js has already pushed when its event
 * fires; page comes from li[data-page] on 'load_more' (the URL follows a
 * frame later) and from ?page otherwise. An update whose promise rejects
 * sends nothing: the list did not change.
 *
 * Observation pauses while an update promise is pending (the re-render
 * replaces the grid's children, which is not a load-more) and re-attaches
 * to the grid the re-render left behind. The document listeners hang on
 * one AbortController, aborted on pagehide together with the observer.
 */
(function (global) {
  'use strict';

  var doc = global.document;
  if (!doc || !global.AbortController) return;

  var script = doc.currentScript || doc.querySelector('script[data-twc-list-id]');
  var listId = (script && script.getAttribute('data-twc-list-id')) || 'search';
  var listName = (script && script.getAttribute('data-twc-list-name')) || listId;

  var raf =
    typeof global.requestAnimationFrame === 'function'
      ? function (fn) {
          return global.requestAnimationFrame(fn);
        }
      : function (fn) {
          return global.setTimeout(fn, 16);
        };

  function params() {
    return new global.URLSearchParams((global.location && global.location.search) || '');
  }

  function pageFrom(p) {
    var n = parseInt(p.get('page'), 10);
    return n > 0 ? n : 1;
  }

  function sortKey(p) {
    return p.get('sort_by') || null;
  }

  function filters(p) {
    var out = [];
    p.forEach(function (value, name) {
      if (name.indexOf('filter.') === 0) out.push({ name: name, value: value });
    });
    return out;
  }

  function query(p) {
    return p.get('q') || null;
  }

  function root() {
    return doc.getElementById('MainContent') || doc.body || doc;
  }

  function cards() {
    return Array.prototype.slice.call(root().querySelectorAll('li[data-product-id]'));
  }

  // "gid://shopify/Product/123" → 123; "123" → 123; anything else → null.
  function idOf(value) {
    var text = String(value == null ? '' : value);
    var tail = text.slice(text.lastIndexOf('/') + 1);
    var n = parseInt(tail, 10);
    return isNaN(n) ? null : n;
  }

  // The first non-empty attribute value on the card element or the li.
  function attr(li, el, name) {
    var value = el ? el.getAttribute(name) : null;
    if (value == null || value === '') value = li.getAttribute(name);
    return value == null || value === '' ? null : value;
  }

  // The ?variant= of the card's product link, or null.
  function linkVariant(li) {
    var a = li.querySelector('a[href*="variant="]');
    if (!a) return null;
    var m = /[?&]variant=(\d+)/.exec(a.getAttribute('href') || '');
    return m ? idOf(m[1]) : null;
  }

  // The price Horizon's card renders as text, as a number, or null. The sale
  // price element wins when present; the compare-at price is never read.
  function renderedPrice(li) {
    var el = li.querySelector('.price-item--sale.price') || li.querySelector('.price');
    if (!el) return null;
    var m = /\d[\d,]*(?:\.\d+)?/.exec(el.textContent || '');
    if (!m) return null;
    var n = Number(m[0].replace(/,/g, ''));
    return isFinite(n) ? n : null;
  }

  // One card → { item, currency }. The payload is Shopify's product view
  // payload; a card whose payload is missing or malformed still reports its
  // product_id from the li. variant_id and price come from the payload's
  // selectedVariant when it has one, else from the card's data-twc-* (see
  // the header), else the link's ?variant= for the id and the rendered
  // price text for the price.
  function read(li, position) {
    var el = li.querySelector('[view-event-payload]');
    var data = null;
    if (el) {
      try {
        data = JSON.parse(el.getAttribute('view-event-payload'));
      } catch (e) {
        data = null;
      }
    }
    var product = (data && data.product) || {};
    var variant = product.selectedVariant || null;
    var priceInfo = variant && variant.price;
    var productId = idOf(product.id);
    if (productId == null) productId = idOf(li.getAttribute('data-product-id'));

    var variantId = variant ? idOf(variant.id) : null;
    if (variantId == null) variantId = idOf(attr(li, el, 'data-twc-variant-id'));
    if (variantId == null) variantId = linkVariant(li);

    var price = priceInfo ? Number(priceInfo.amount) : NaN;
    if (!isFinite(price)) {
      var stated = attr(li, el, 'data-twc-price');
      price = stated == null ? NaN : Number(stated);
    }
    if (!isFinite(price)) {
      var rendered = renderedPrice(li);
      price = rendered == null ? NaN : rendered;
    }

    return {
      currency: (priceInfo && priceInfo.currencyCode) || null,
      item: {
        product_id: productId,
        variant_id: variantId,
        title: product.title || null,
        handle: product.handle || null,
        price: isFinite(price) ? price : null,
        position: position,
      },
    };
  }

  // Every card in the grid, in order: [{ li, item }] plus the list's currency.
  function snapshot() {
    var list = cards();
    var entries = [];
    var currency = null;
    for (var i = 0; i < list.length; i++) {
      var r = read(list[i], i + 1);
      if (!currency && r.currency) currency = r.currency;
      entries.push({ li: list[i], item: r.item });
    }
    if (!currency) {
      currency = (global.Shopify && global.Shopify.currency && global.Shopify.currency.active) || null;
    }
    return { entries: entries, currency: currency };
  }

  function emit(trigger, items, page, currency) {
    var track = global.twcTrack;
    if (typeof track !== 'function' || !items.length) return;
    var p = params();
    try {
      track('view_item_list', {
        list_id: listId,
        list_name: listName,
        trigger: trigger,
        page: page,
        sort_key: sortKey(p),
        filters: filters(p),
        query: query(p),
        currency: currency,
        items: items,
        page_path: (global.location && global.location.pathname) || null,
      });
    } catch (e) {
      /* analytics never breaks the page */
    }
  }

  var seen = new global.WeakSet();
  var lastSort = sortKey(params());
  var pending = null;
  var observer = null;
  var observed = null;
  var frame = 0;

  // The whole grid as one list; every card is then "seen".
  function full(trigger) {
    var snap = snapshot();
    var items = [];
    seen = new global.WeakSet();
    for (var i = 0; i < snap.entries.length; i++) {
      seen.add(snap.entries[i].li);
      items.push(snap.entries[i].item);
    }
    emit(trigger, items, pageFrom(params()), snap.currency);
  }

  // The cards that arrived since the last reading, with their grid positions.
  function appended() {
    var snap = snapshot();
    var fresh = [];
    var page = null;
    for (var i = 0; i < snap.entries.length; i++) {
      var li = snap.entries[i].li;
      if (seen.has(li)) continue;
      seen.add(li);
      fresh.push(snap.entries[i].item);
      if (page == null) {
        var n = parseInt(li.getAttribute('data-page'), 10);
        if (n > 0) page = n;
      }
    }
    if (!fresh.length) return;
    emit('load_more', fresh, page || pageFrom(params()), snap.currency);
  }

  function onMutation() {
    if (pending || frame) return;
    frame = raf(function () {
      frame = 0;
      if (pending) return;
      appended();
    });
  }

  function observeGrid() {
    var grid = root().querySelector('[ref="grid"]');
    if (grid === observed && observer) return;
    if (observer) observer.disconnect();
    observer = null;
    observed = grid;
    if (!grid || typeof global.MutationObserver !== 'function') return;
    observer = new global.MutationObserver(onMutation);
    observer.observe(grid, { childList: true });
  }

  function onUpdate(event) {
    var promise = event && event.promise ? Promise.resolve(event.promise) : Promise.resolve();
    pending = promise;
    promise
      .then(
        function () {
          return true;
        },
        function () {
          return false;
        }
      )
      .then(function (ok) {
        if (pending !== promise) return; // a newer update superseded this one
        raf(function () {
          if (pending !== promise) return;
          pending = null;
          if (ok) {
            var sortNow = sortKey(params());
            var trigger = sortNow !== lastSort ? 'sort' : 'filter';
            lastSort = sortNow;
            full(trigger);
          }
          observeGrid();
        });
      });
  }

  var controller = new global.AbortController();
  var signal = controller.signal;
  doc.addEventListener('shopify:collection:update', onUpdate, { signal: signal });
  doc.addEventListener('shopify:search:update', onUpdate, { signal: signal });
  global.addEventListener(
    'pagehide',
    function () {
      controller.abort();
      if (observer) observer.disconnect();
      observer = null;
      pending = null;
    },
    { signal: signal }
  );

  full('load');
  observeGrid();
})(window);
